/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  IRowNode,
  SideBarDef,
} from '@ag-grid-community/core';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountGroupOutlineIcon,
  CloseIcon,
  ContentSaveIcon,
  DownloadIcon,
  FilterVariantIcon,
  RefreshIcon,
  PostLoadFilledIcon,
} from '@atgfr8/icons';
import {
  MenuItemType,
  SelectInputCustom,
  gridStateWithAdditionalProps,
} from '@components';
import { BasicModal } from '@components/Modal/BasicModal';
import { MouseOverPopover } from '@components/Popover/MouseOverPopover';
import { FeatureFlags } from '@constants/featureFlags';
import {
  checkUsersPostingValid,
  getEffectiveIsEnabledForUserByFeatureName,
} from '@store/Global/service';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  FilterPassT,
  ILoadBoardViewModel,
  SavedSearchCategory,
  appendNewSavedSearch,
  columnDefsMap,
  loadBoardStore,
  saveLoadboardCache,
  setLoadBoardStore,
} from '@store/loadboard';
import { BULK_SHARED_CAPACITY_LIMIT } from '@store/loadboard/bulkSharedCapacity';
import { saveFilter } from '@store/loadboard/services';
import {
  clearRelativeDates,
  relativeDatesForGrid,
  relativeDatesForSave,
} from '@store/loadboard/utils';
import { userStore } from '@store/user';
import {
  doesExternalFilterFactory,
  findMenuItemByValue,
  getSortModelFromColumnState,
  isExternalFilterPresent,
  printLog,
} from '@utils/utils';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { debounce, isEqual } from 'lodash';
import {
  Accessor,
  Component,
  Show,
  createEffect,
  createSignal,
  onMount,
} from 'solid-js';
import { effect } from 'solid-js/web';

import { closeModal, isModalOpen, openModal } from '../../../store/modals';
import { ManageModal } from './ManageModal';
import { MassSharedCapacityModal } from './MassSharedCapacityModal';
import { PostUnPostLoadModal } from './PostUnPostLoadModal';
import { SaveModal } from './SaveModal';
import classes from './classes';
import {
  FilterT,
  columnsToolPanel,
  filterByItems,
  filterRulesByValue,
  filtersToolPanel,
} from './constants';

const defaultItem = {
  label: '',
  value: '',
};

const firstDisplayedShareLoads = (
  gridApi: GridApi | undefined,
): ILoadBoardViewModel[] => {
  if (gridApi === undefined) {
    return [];
  }

  const loads: ILoadBoardViewModel[] = [];
  gridApi.forEachNodeAfterFilterAndSort(
    (row: IRowNode<ILoadBoardViewModel>) => {
      if (
        loads.length >= BULK_SHARED_CAPACITY_LIMIT ||
        row.data === undefined
      ) {
        return;
      }

      loads.push(row.data);
    },
  );

  return loads;
};

type FiltersProps = {
  gridReady: boolean;
  gridOptions: GridOptions;
  columnDefs: ColDef[];
  loading: boolean;
  gridApi?: GridApi;
  onRefreshGrid?: () => void;
  customFilterCache: Accessor<string>;
  setCustomFilterCache: (filter: string) => void;
  onUpdatePostings: (postings: ILoadBoardViewModel[]) => void;
  showOptimized?: boolean;
  setRestrictToMyUserId?: (n: number) => void;
};

const Filters: Component<FiltersProps> = (props: FiltersProps) => {
  const [filterBy, setFilterBy] = createSignal<MenuItemType>(defaultItem);
  const [quickSearchString, setQuickSearchString] = createSignal('');
  const [advancedFilters, setAdvancedFilters] = createSignal(false);
  const [filterSaveError, setFilterSaveError] = createSignal(false);
  const [prepopulatedComment, setPrepopulatedComment] = createSignal(false);
  const [menuItems, setMenuItems] = createSignal<MenuItemType[]>([]);
  const [postUnpost, setPostUnpost] = createSignal<'none' | 'post' | 'unpost'>(
    'none',
  );
  const [loaded, setLoaded] = createSignal(false);
  const [rowCount, setRowCount] = createSignal(0);
  const [shareLoads, setShareLoads] = createSignal<ILoadBoardViewModel[]>([]);
  const massShareCapacityModalId = 'massShareCapacityModal';
  const massShareCapacityEnabled = isFeatureFlagEnabled(
    FeatureFlags.MassShareCapacity,
  );

  const updateRowCount = () => {
    void setRowCount(props.gridApi?.getDisplayedRowCount() ?? 0);
  };

  const debouncedUpdateRowCount = debounce(updateRowCount, 750);

  const [myFilters, setMyFilters] = createSignal(
    loadBoardStore.savedSearches.filter((s) => {
      return (
        s.category === SavedSearchCategory.Basic ||
        s.category === SavedSearchCategory.Advanced
      );
    }),
  );
  const [filterPass, setFilterPass] = createSignal<FilterPassT>({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fn: (node: IRowNode) => {
      return true;
    },
    type: undefined,
    filter: '',
  });

  const debouncedApplyQuickFilter = debounce(
    (api: GridApi, filterText: string) => {
      clearFilters();
      api.setGridOption('quickFilterText', filterText);
    },
    500,
  );

  const applyQuickSearch = (text?: string | null) => {
    const safeText = text ?? '';
    setQuickSearchString(safeText);
    props.gridApi?.setGridOption('quickFilterText', safeText);
  };

  effect(() => {
    setMenuItems([
      { value: '', label: 'Select' },
      {
        value: '',
        label: 'MY FILTERS',
        disabled: true,
      },
      ...myFilters().map((f) => {
        const label =
          f.category === SavedSearchCategory.Advanced ? ' (Adv)' : '';
        if (f.name.length > 15) {
          return {
            value: `my:${f.name}`,
            label: `${f.name.slice(0, 15)}...${label}`,
          };
        }
        return {
          value: `my:${f.name}`,
          label: `${f.name}${label}`,
        };
      }),
      ...filterByItems,
      {
        value: '',
        label: 'MY GROUP',
        disabled: true,
      },
      ...Object.keys(loadBoardStore.membersByGroupName).map((key) => {
        return {
          value: `by_Group:${key}`,
          label: key,
        };
      }),
      {
        value: '',
        label: 'MY CUSTOMER GROUP',
        disabled: true,
      },
      ...Object.keys(loadBoardStore.officeGroupIdsByMachingNames)
        .filter((g) => g != 'The Office' && g != 'Managers')
        .map((key) => {
          return {
            value: `by_Customer:${key}`,
            label: key,
          };
        }),
    ]);
  });

  onMount(async () => {
    const prepopulatedComment = await getEffectiveIsEnabledForUserByFeatureName(
      'PrepopulateOrderIdOnMultiPostingCommentEnabled',
    );

    setPrepopulatedComment(Boolean(prepopulatedComment));

    const selectedFilter = sessionStorage.getItem('selectedFilter');
    if (Boolean(selectedFilter)) {
      try {
        setFilterBy(JSON.parse(selectedFilter as string));
      } catch (e) {
        const item = findMenuItemByValue(selectedFilter as string, menuItems());

        setFilterBy(item);
        sessionStorage.setItem('selectedFilter', JSON.stringify(filterBy()));
      }
    }

    props.gridApi?.addEventListener('filterChanged', debouncedUpdateRowCount);
    props.gridApi?.addEventListener('rowDataUpdated', debouncedUpdateRowCount);
    props.gridApi?.addEventListener('modelUpdated', debouncedUpdateRowCount);
  });

  createEffect(() => {
    if (props.gridApi === undefined) {
      return;
    }

    props.gridApi.setGridOption(
      'isExternalFilterPresent',
      isExternalFilterPresent,
    );

    props.gridApi.setGridOption('doesExternalFilterPass', filterPass().fn);
  });

  createEffect(() => {
    if (props.gridReady && props.gridApi !== undefined) {
      const searchFilter = props.gridApi.getQuickFilter() ?? '';
      setQuickSearchString(searchFilter);
    }

    void updateRowCount();
  });

  createEffect(() => {
    if (!props.gridReady || props.gridApi === undefined) {
      return;
    }

    loadBoardStore.items.length;
    updateRowCount();
  });

  const saveFilterModalId = 'saveFilterModal';
  const manageSavedTemplatesId = 'manageSavedTemplates';
  const postUnpostLoadModalId = 'postUnpostLoadModal';

  // Approve by jeremy
  // eslint-disable-next-line complexity
  const onFilterByChange = (value: string) => {
    if (props.gridApi === undefined) {
      return;
    }
    debouncedApplyQuickFilter.cancel();
    const myFilter = value.match(/my:(.*$)/);
    const byGroupFilter = value.match(/by_Group:(.*$)/);
    const byCustomerFilter = value.match(/by_Customer:(.*$)/);

    if (myFilter === null) {
      applyQuickSearch('');
    }

    const selectedFilter = findMenuItemByValue(value, menuItems());
    sessionStorage.setItem('selectedFilter', JSON.stringify(selectedFilter));
    setFilterBy(selectedFilter);

    if (value === '') {
      props.gridApi.setFilterModel(null);
      props.gridApi.setAdvancedFilterModel(null);
      return;
    } else if (myFilter !== null) {
      props.gridApi.setGridOption('isExternalFilterPresent', () => false);
      const filterName = myFilter[1];
      const filter = loadBoardStore.savedSearches.find((s) => {
        return s.name === filterName;
      });

      if (filter !== undefined) {
        if (
          (filter.category === SavedSearchCategory.Basic &&
            advancedFilters()) ||
          (filter.category === SavedSearchCategory.Advanced &&
            !advancedFilters())
        ) {
          onAdvancedFiltersEnabled();
        }

        const gridState = JSON.parse(
          filter.params,
        ) as gridStateWithAdditionalProps;
        if (gridState.rowGroup !== undefined) {
          props.gridApi.setRowGroupColumns(gridState.rowGroup.groupColIds);
        }

        applyQuickSearch(gridState.quickSearch);

        if (gridState.columnPinning !== undefined) {
          gridState.columnPinning.leftColIds.forEach((col) => {
            props.gridApi?.setColumnPinned(col, 'left');
          });
          gridState.columnPinning.rightColIds.forEach((col) => {
            props.gridApi?.setColumnPinned(col, 'right');
          });
        }

        if (gridState.filter !== undefined) {
          const columnFilterModel = gridState.filter.filterModel;
          const hasColumnFilters =
            columnFilterModel != null &&
            Object.keys(columnFilterModel).length > 0;

          if (hasColumnFilters) {
            clearAdvancedFilters();
            props.gridApi.setFilterModel(
              relativeDatesForGrid(columnFilterModel),
            );
          } else if (gridState.filter.advancedFilterModel) {
            props.gridApi.setFilterModel(null);
            props.gridApi.setAdvancedFilterModel(
              gridState.filter.advancedFilterModel,
            );
          } else {
            clearAdvancedFilters();
            props.gridApi.setFilterModel(null);
          }
        } else {
          props.gridApi.setFilterModel(null);
        }

        //If user is in a group and postingId is not included in the order colId due to preexisting cache, add it to the front of the array
        if (
          !Boolean(
            gridState.columnOrder?.orderedColIds.includes('postingId'),
          ) &&
          Boolean(userStore.user.postingGroupId)
        ) {
          gridState.columnOrder?.orderedColIds.unshift('postingId');
        }

        if (gridState.sideBar !== undefined) {
          props.gridApi.setSideBarVisible(gridState.sideBar.visible);
          props.gridApi.setSideBarPosition(gridState.sideBar.position);
        }

        if (gridState.columnOrder !== undefined) {
          const newColumnDefs = gridState.columnOrder.orderedColIds
            .map((col) => {
              return columnDefsMap[col];
            })
            // eslint-disable-next-line  @typescript-eslint/no-unnecessary-condition
            .filter((v) => v !== undefined)
            .filter((v) => {
              //If user is not in a group, return all columns except postingId
              if (!Boolean(userStore.user.postingGroupId)) {
                return v.field !== 'postingId';
              }

              //Otherwise, return all columns
              return v;
            });

          //now merge in ones that are not in the ordered list
          props.columnDefs.forEach((col) => {
            if (!newColumnDefs.find((def) => def.field === col.field)) {
              newColumnDefs.push(col);
            }
          });
          props.gridApi.setGridOption('columnDefs', newColumnDefs);
        }

        if (gridState.sort !== undefined) {
          props.gridApi.applyColumnState({
            state: [...gridState.sort.sortModel],
            defaultState: { sort: null },
          });
        }

        if (gridState.columnVisibility !== undefined) {
          const { visible, hidden } = props.columnDefs.reduce(
            (acc, col) => {
              const isHidden = (
                gridState.columnVisibility ?? { hiddenColIds: [] }
              ).hiddenColIds.includes(col.field ?? '');

              if (isHidden) {
                acc.hidden.push(col.field ?? '');
              } else {
                acc.visible.push(col.field ?? '');
              }

              return acc;
            },
            {
              visible: [] as string[],
              hidden: [] as string[],
            },
          );

          props.gridApi.setColumnsVisible(hidden, false);
          props.gridApi.setColumnsVisible(visible, true);
        } else {
          const visibility = props.columnDefs
            .map((def) => def.field)
            .filter((def) => def !== undefined);

          props.gridApi.setColumnsVisible(visibility, true);
        }

        if (gridState.columnSizing !== undefined) {
          props.gridApi.setColumnWidths(
            gridState.columnSizing.columnSizingModel.map((size) => {
              return {
                key: size.colId,
                newWidth: size.width !== undefined ? size.width : 100,
              };
            }),
          );
        }
      } else {
        applyQuickSearch('');
      }

      props.setCustomFilterCache('');
      return;
    } else if (byGroupFilter !== null) {
      const groupName = byGroupFilter[1];
      setFilterPass({
        fn: doesExternalFilterFactory(groupName, 'group'),
        type: 'group',
        filter: groupName,
      });

      props.gridApi.setFilterModel(null);
      clearAdvancedFilters();
      props.gridApi.onFilterChanged();
      props.setCustomFilterCache(value);
      return;
    } else if (byCustomerFilter !== null) {
      setFilterPass({
        fn: doesExternalFilterFactory(byCustomerFilter[1], 'customer'),
        type: 'customer',
        filter: byCustomerFilter[1],
      });

      props.gridApi.setFilterModel(null);
      clearAdvancedFilters();
      props.gridApi.onFilterChanged();
      props.setCustomFilterCache(value);
      return;
    }

    const rules = filterRulesByValue[value] as FilterT[] | undefined;

    if (rules !== undefined) {
      const filterModel: Record<string, unknown> = {};

      for (const rule of rules) {
        filterModel[rule.label] = rule.getFilter();
        // @ts-expect-error test
        filterModel.testFilter = filterModel[rule.label].params;
      }

      clearAdvancedFilters();
      props.gridApi.setGridOption('isExternalFilterPresent', () => false);
      props.setCustomFilterCache(value);
      props.gridApi.setFilterModel(
        relativeDatesForGrid(filterModel as FilterModel),
      );
    }
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters(false);
    props.gridApi?.setAdvancedFilterModel(null);
    props.gridOptions.enableAdvancedFilter = false;
    props.gridApi?.setGridOption('enableAdvancedFilter', false);
  };

  const clearFilters = () => {
    if (props.gridApi === undefined) {
      return;
    }

    debouncedApplyQuickFilter.cancel();
    clearRelativeDates();
    props.gridApi.setFilterModel(null);
    props.gridApi.setAdvancedFilterModel(null);
    props.gridApi.setGridOption('isExternalFilterPresent', () => false);
    props.setCustomFilterCache('');
    sessionStorage.removeItem('savedSearches');
    sessionStorage.removeItem('selectedFilter');
    saveLoadboardCache(undefined);
    setFilterBy(defaultItem);
  };

  const clearAllFilters = (activeFilter?: string) => {
    if (props.gridApi === undefined) {
      return;
    }

    // This api call clears the sorting from the columns.
    props.gridApi.applyColumnState({
      state: [],
      defaultState: { sort: null },
    });

    if (activeFilter === undefined) {
      setQuickSearchString('');
      debouncedApplyQuickFilter.cancel();
      props.gridApi.setGridOption('quickFilterText', '');
      clearFilters();
      return;
    }

    const myFilter = activeFilter.match(/my:(.*$)/);
    const rules = filterRulesByValue[activeFilter] as FilterT[] | undefined;

    setFilterBy(defaultItem);
    sessionStorage.removeItem('selectedFilter');

    if (rules !== undefined) {
      // This logic might break if we have hard-coded filters that are advanced,
      // rather than basic. We should revisit this if this becomes an issue.
      const filterModel = { ...props.gridApi.getFilterModel() };

      for (const rule of rules) {
        delete filterModel[rule.label];
      }

      props.gridApi.setFilterModel(filterModel);
    } else if (myFilter !== null) {
      props.gridApi.setFilterModel(null);
      props.gridApi.setAdvancedFilterModel(null);
    }
  };

  const onFilterModelSave = async (name: string, cb: () => void) => {
    if (props.gridApi === undefined) {
      return;
    }

    let category = SavedSearchCategory.Basic;
    let customFilter: string | undefined = props.customFilterCache();

    if (!advancedFilters()) {
      const filters = props.gridApi.getFilterModel();

      for (const key in filters) {
        if (
          (key === 'pickDate' && customFilter.startsWith('pickBy')) ||
          (key === 'dropDate' && customFilter.startsWith('dropBy'))
        ) {
          const ogFilter = filters[key];
          const filter = filterRulesByValue[customFilter][0]?.getFilter();
          // @ts-expect-error params can exist here.
          delete filter.params;

          if (!isEqual(ogFilter, filter)) {
            customFilter = '';
            props.setCustomFilterCache(customFilter);
          }
        }
      }
    } else {
      category = SavedSearchCategory.Advanced;
    }

    const gridState = props.gridApi.getState();
    const columns = props.gridApi.getColumnState();
    const quickSearch = props.gridApi.getQuickFilter() ?? '';
    const stampedFilterModel = relativeDatesForSave(
      props.gridApi.getFilterModel(),
    );
    const completeState = {
      ...gridState,
      filter: {
        ...(gridState.filter ?? { filterModel: null }),
        filterModel: stampedFilterModel,
      },
      quickSearch,
      customFilter,
      myItems: loadBoardStore.myItems,
      sort: {
        sortModel: getSortModelFromColumnState(columns),
      },
    };

    try {
      const newFilter = await saveFilter(completeState, category, name);
      newFilter.userId = userStore.user.id;
      appendNewSavedSearch(newFilter);
      closeModal(saveFilterModalId);
    } catch (e) {
      printLog(e);
      setFilterSaveError(true);
    } finally {
      cb();
    }
  };

  const onAdvancedFiltersEnabled = () => {
    const newVal = !advancedFilters();
    setAdvancedFilters(newVal);

    if (props.gridApi === undefined) {
      return;
    }

    props.gridApi.setFilterModel(null);
    props.gridApi.refreshCells();
    props.gridOptions.enableAdvancedFilter = newVal;
    props.gridApi.setGridOption('enableAdvancedFilter', newVal);

    const sideBar: SideBarDef = {};

    if (!newVal) {
      sideBar.toolPanels = [filtersToolPanel, columnsToolPanel];
    } else {
      sideBar.toolPanels = [columnsToolPanel];
    }

    props.gridApi.setGridOption('sideBar', sideBar);
  };

  const setCustomerFilter = (filter: string) => {
    const gridState = loadBoardStore.cache
      ? (JSON.parse(
          loadBoardStore.cache.params,
        ) as gridStateWithAdditionalProps)
      : { customFilter: undefined };
    const currentFilterBy = filterBy().value;

    const selectedFilter = findMenuItemByValue(filter, menuItems());
    sessionStorage.setItem('selectedFilter', JSON.stringify(selectedFilter));
    setFilterBy(selectedFilter);

    const byGroupFilter = filter.match(/by_Group:(.*$)/);
    const byCustomerFilter = filter.match(/by_Customer:(.*$)/);

    if (byGroupFilter !== null && props.gridApi !== undefined) {
      const groupName = byGroupFilter[1];

      if (filterPass().type !== 'group') {
        setFilterPass({
          fn: doesExternalFilterFactory(groupName, 'group'),
          type: 'group',
          filter: groupName,
        });
      }

      if (currentFilterBy === filter && gridState.customFilter === filter) {
        return;
      }

      props.gridApi.onFilterChanged();
      props.setCustomFilterCache(filter);
    } else if (byCustomerFilter !== null && props.gridApi !== undefined) {
      if (filterPass().type !== 'customer') {
        setFilterPass({
          fn: doesExternalFilterFactory(byCustomerFilter[1], 'customer'),
          type: 'customer',
          filter: byCustomerFilter[1],
        });
      }

      if (currentFilterBy === filter && gridState.customFilter === filter) {
        return;
      }

      props.gridApi.onFilterChanged();
    }
  };

  //Remove the stale filter from the cache
  const clearStaleCachedCustomFilter = (
    gridState: gridStateWithAdditionalProps,
  ) => {
    setFilterBy(defaultItem);
    sessionStorage.removeItem('selectedFilter');
    props.setCustomFilterCache('');

    setFilterPass({
      fn: () => true,
      type: undefined,
      filter: '',
    });
    props.gridApi?.onFilterChanged();

    if (loadBoardStore.cache !== undefined) {
      saveLoadboardCache({
        ...loadBoardStore.cache,
        params: JSON.stringify({
          ...gridState,
          customFilter: '',
        }),
      });
    }
  };

  const isCachedCustomFilterStillValid = (customFilter: string): boolean => {
    const byGroupFilter = customFilter.match(/by_Group:(.*$)/);
    if (byGroupFilter !== null) {
      return loadBoardStore.membersByGroupName[byGroupFilter[1]] !== undefined;
    }

    const byCustomerFilter = customFilter.match(/by_Customer:(.*$)/);
    if (byCustomerFilter !== null) {
      return (
        loadBoardStore.officeGroupIdsByMachingNames[byCustomerFilter[1]] !==
        undefined
      );
    }

    const myFilter = customFilter.match(/my:(.*$)/);
    if (myFilter !== null) {
      return loadBoardStore.savedSearches.some((s) => s.name === myFilter[1]);
    }

    return true;
  };

  effect(() => {
    if (loadBoardStore.cache === undefined) {
      return;
    }
    const gridState = JSON.parse(
      loadBoardStore.cache.params,
    ) as gridStateWithAdditionalProps;
    if (gridState.customFilter) {
      if (
        typeof gridState.customFilter === 'string' &&
        !isCachedCustomFilterStillValid(gridState.customFilter)
      ) {
        clearStaleCachedCustomFilter(gridState);
        return;
      }

      const selectedFilter = findMenuItemByValue(
        gridState.customFilter,
        menuItems(),
      );
      sessionStorage.setItem('selectedFilter', JSON.stringify(selectedFilter));

      if (
        typeof gridState.customFilter == 'string' &&
        (gridState.customFilter.includes('my:') ||
          gridState.customFilter.includes('by_Group:') ||
          gridState.customFilter.includes('by_Customer:'))
      ) {
        setFilterBy(selectedFilter);
        setCustomerFilter(gridState.customFilter);
      } else if (gridState.customFilter) {
        setFilterBy(selectedFilter);
      }
    }
  });

  // Approve by James
  // eslint-disable-next-line complexity
  effect(() => {
    if (filterSaveError() && !isModalOpen(saveFilterModalId)) {
      setFilterSaveError(false);
    }

    if (loadBoardStore.cache !== undefined) {
      const gridState = JSON.parse(
        loadBoardStore.cache.params,
      ) as gridStateWithAdditionalProps;

      if (
        gridState.filter !== undefined &&
        props.gridApi !== undefined &&
        !loaded()
      ) {
        if (gridState.filter.advancedFilterModel !== undefined) {
          if (!advancedFilters()) {
            onAdvancedFiltersEnabled();
          }

          props.gridApi.setAdvancedFilterModel(
            gridState.filter.advancedFilterModel,
          );
        } else if (gridState.filter.filterModel !== undefined) {
          if (advancedFilters()) {
            onAdvancedFiltersEnabled();
          }

          props.gridApi.setFilterModel(
            relativeDatesForGrid(gridState.filter.filterModel),
          );
        }

        props.gridApi.refreshCells();
        setLoaded(true);
      }
    }

    setMyFilters(loadBoardStore.filters);
  });

  return (
    <Stack spacing={2}>
      <Box
        class="flex flex-nowrap gap-3 items-center"
        style={{
          'background-color': '#fff',
          padding: '10px',
        }}
      >
        <TextField
          label="Find..."
          dataTestId={'find-textInput'}
          id="full-text-find"
          class="[&.input]:h-[37px]!"
          style={{
            'max-width': '200px',
            background: 'white',
          }}
          value={quickSearchString()}
          onChange={(value: string) => {
            if (props.gridApi !== undefined) {
              setQuickSearchString(value);
              debouncedApplyQuickFilter(props.gridApi, value);
            }
          }}
        />
        <div class="w-[260px]">
          <SelectInputCustom
            label="Filters"
            dataTestId="loadboard-filter-select"
            value={filterBy().value}
            menuItems={menuItems()}
            size="sm"
            renderValue={(value) => {
              const selected = Array.isArray(value) ? (value[0] ?? '') : value;
              if (selected.includes(':')) {
                return selected.split(':')[1];
              }

              const selectedOption = filterByItems.find((filter) => {
                return filter.value === selected;
              });

              return selectedOption ? selectedOption.label : selected;
            }}
            onChange={(value) => {
              onFilterByChange(value);
              updateRowCount();
            }}
          />
        </div>
        <IconButton
          dataTestId={formatDataTestId('Manage Filters', 'button')}
          aria-label="Manage"
          title="Manage Filters"
          size="md"
          color="info"
          onClick={() => openModal(manageSavedTemplatesId)}
        >
          <FilterVariantIcon />
        </IconButton>
        <IconButton
          dataTestId={formatDataTestId('Save Filters', 'button')}
          aria-label="Save"
          title="Save Filters"
          size="md"
          color="info"
          on:click={() => openModal(saveFilterModalId)}
        >
          <ContentSaveIcon />
        </IconButton>
        <IconButton
          dataTestId={formatDataTestId('Refresh Loadboard', 'button')}
          aria-label="Refresh"
          title="Refresh Loadboard"
          size="md"
          color="info"
          on:click={() => props.onRefreshGrid?.()}
          disabled={props.loading}
          style={
            props.loading
              ? {
                  'margin-left': '2px',
                  'margin-right': '2px',
                }
              : undefined
          }
        >
          {props.loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
        <IconButton
          dataTestId={formatDataTestId('Clear All Filters', 'button')}
          aria-label="Clear All Filters"
          title="Clear All Filters"
          size="md"
          color="info"
          aria-owns=""
          aria-haspopup="true"
          on:click={() => {
            clearAllFilters();
            updateRowCount();
          }}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          dataTestId={formatDataTestId('Post Loads', 'button')}
          aria-label="Post load(s)"
          title="Post load(s)"
          size="md"
          color="info"
          aria-owns=""
          aria-haspopup="true"
          on:click={async () => {
            await checkUsersPostingValid();
            setPostUnpost('post');
            openModal(postUnpostLoadModalId);
          }}
        >
          <PostLoadFilledIcon />
        </IconButton>
        <IconButton
          dataTestId={formatDataTestId('Unpost Loads', 'button')}
          aria-label="Unpost load(s)"
          title="Unpost load(s)"
          size="md"
          color="info"
          aria-owns=""
          aria-haspopup="true"
          on:click={async () => {
            await checkUsersPostingValid();
            setPostUnpost('unpost');
            openModal(postUnpostLoadModalId);
          }}
        >
          <DownloadIcon />
        </IconButton>
        <Show when={massShareCapacityEnabled()}>
          <IconButton
            dataTestId={formatDataTestId('Add Shared Capacity', 'button')}
            aria-label="Add Shared Capacity"
            title="Add Shared Capacity"
            size="md"
            color="info"
            on:click={() => {
              setShareLoads(firstDisplayedShareLoads(props.gridApi));
              openModal(massShareCapacityModalId);
            }}
          >
            <AccountGroupOutlineIcon />
          </IconButton>
        </Show>
        <div class="flex items-center">
          <Checkbox
            dataTestId={formatDataTestId('Advanced Filters', 'checkboxInput')}
            label="Advanced Filters"
            checked={advancedFilters()}
            onChange={() => {
              onAdvancedFiltersEnabled();
              updateRowCount();
            }}
          />
        </div>
        <div class="flex items-center">
          <Checkbox
            dataTestId={formatDataTestId('Show My Items', 'checkboxInput')}
            label="Show My Items"
            checked={loadBoardStore.myItems}
            onChange={async () => {
              setLoadBoardStore('myItems', !loadBoardStore.myItems);
              props.gridApi?.onFilterChanged();
              await props.onRefreshGrid?.();
              updateRowCount();
            }}
          />
        </div>
        <Show
          when={
            isFeatureFlagEnabled(FeatureFlags.AllowEmptyInLoadboardViewsUI)() &&
            !props.showOptimized
          }
        >
          <MouseOverPopover message="View empty loads modified in the last 6 months">
            <Checkbox
              dataTestId={formatDataTestId('Show Only Empty', 'checkboxInput')}
              label="Show Only Empty"
              checked={loadBoardStore.onlyEmpty}
              onChange={async () => {
                setLoadBoardStore('onlyEmpty', !loadBoardStore.onlyEmpty);
                clearFilters();
                await props.onRefreshGrid?.();
              }}
            />
          </MouseOverPopover>
        </Show>
        <Show when={!props.showOptimized}>
          <div class={`${classes.totalAmount}`}>
            <div class={`${classes.totalAmountRegularFont2} `}>Load Count</div>
            <div class={`${classes.totalAmountRegularFont}`}>{rowCount()}</div>
          </div>
        </Show>
      </Box>
      <ManageModal id={manageSavedTemplatesId} onCloseModal={closeModal} />
      <SaveModal
        id={saveFilterModalId}
        onCloseModal={closeModal}
        onSave={onFilterModelSave}
        hasError={filterSaveError()}
      />
      <div style={{ display: postUnpost() !== 'none' ? 'block' : 'none' }}>
        <PostUnPostLoadModal
          id={postUnpostLoadModalId}
          action={postUnpost()}
          onCloseModal={closeModal}
          refreshGrid={props.onRefreshGrid}
          prepopulatedComment={prepopulatedComment()}
          onUpdatePostings={props.onUpdatePostings}
        />
      </div>
      <BasicModal
        title="Add Shared Capacity"
        id={massShareCapacityModalId}
        onClose={() => {
          closeModal(massShareCapacityModalId);
        }}
        width="100%"
        maxWidth="960px"
        class="mass-share-capacity"
        showClose
      >
        <MassSharedCapacityModal
          id={massShareCapacityModalId}
          loads={shareLoads()}
          onClose={closeModal}
          refreshGrid={props.onRefreshGrid}
        />
      </BasicModal>
    </Stack>
  );
};

export default Filters;
